import React from 'react';
import {
  MessageSquare, Globe, ExternalLink, ShoppingBag, MapPin,
  Star, CheckCircle2, Eye, Phone, Tag, Youtube, Instagram, Heart, Calendar
} from 'lucide-react';

interface BiolinkPhoneFrameProps {
  title?: string;
  category?: string;
}

export const BiolinkPhoneFrame: React.FC<BiolinkPhoneFrameProps> = ({
  title = "Bruta Burger & Craft",
  category = "Fast Food · Chapecó, SC"
}) => {
  return (
    <div className="w-[280px] bg-[#1C1C1E] rounded-[48px] border-[6px] border-[#2C2C2E] relative overflow-hidden font-sans text-left flex flex-col mx-auto" style={{ height: 520 }}>

      {/* Dynamic Island */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[90px] h-[22px] bg-black rounded-full z-50 flex items-center justify-between px-2.5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#1a1a1a] border border-[#333]" />
        <div className="w-1.5 h-1.5 rounded-full bg-[#00C851]" />
      </div>

      {/* Screen */}
      <div className="w-full h-full bg-white flex flex-col overflow-hidden rounded-[42px]">

        {/* COVER PHOTO with gradient overlay */}
        <div className="relative h-28 shrink-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=500&q=80&auto=format&fit=crop"
            className="w-full h-full object-cover"
            alt="Estabelecimento"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/60" />
          {/* Rating badge top right */}
          <div className="absolute top-6 right-3 bg-white rounded-lg px-1.5 py-0.5 flex items-center gap-0.5 shadow-sm">
            <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
            <span className="text-[9px] font-black text-slate-900">4.9</span>
          </div>
        </div>

        {/* PROFILE HEADER overlapping cover */}
        <div className="px-4 pt-0 pb-3 -mt-8 relative z-10 flex flex-col items-center text-center space-y-1 border-b border-slate-100">
          {/* Avatar */}
          <div className="w-14 h-14 rounded-full border-[3px] border-white overflow-hidden shadow-md">
            <img
              src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&q=80&auto=format&fit=crop"
              className="w-full h-full object-cover"
              alt="Avatar"
            />
          </div>
          <div className="absolute bottom-4 right-3 -translate-y-8">
            <span className="text-[8px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">● Online</span>
          </div>

          <strong className="text-xs font-black text-slate-900 flex items-center gap-1 pt-1">
            {title} <span className="text-[#0061FF] text-[10px]">✓</span>
          </strong>
          <span className="text-[9px] text-slate-500 font-medium">{category}</span>

          <div className="flex items-center justify-center gap-1 text-[8px] text-slate-400">
            <Eye className="w-2.5 h-2.5 text-[#0061FF]" /> 1.842 views hoje
            <span className="mx-1">·</span>
            <Heart className="w-2.5 h-2.5 text-rose-400" /> 234 favoritos
          </div>

          {/* Social icons */}
          <div className="flex gap-2 pt-1">
            {[
              { icon: Instagram, bg: 'bg-pink-50 text-pink-500' },
              { icon: Youtube,   bg: 'bg-rose-50 text-rose-600' },
              { icon: Globe,     bg: 'bg-blue-50 text-blue-600' },
              { icon: Phone,     bg: 'bg-emerald-50 text-emerald-600' },
            ].map((s, i) => (
              <div key={i} className={`w-7 h-7 ${s.bg} rounded-xl flex items-center justify-center border border-slate-100`}>
                <s.icon className="w-3.5 h-3.5" />
              </div>
            ))}
          </div>
        </div>

        {/* LINK STACK */}
        <div className="px-3 py-2.5 space-y-2 flex-1 overflow-y-auto no-scrollbar">
          {/* Primary CTA */}
          <div className="bg-[#0061FF] text-white p-2.5 rounded-xl flex justify-between items-center">
            <div className="flex items-center gap-1.5 text-[10px] font-bold">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Pedir via WhatsApp</span>
            </div>
            <ExternalLink className="w-3 h-3 opacity-70" />
          </div>

          {/* Product preview card */}
          <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden flex">
            <div className="w-16 h-16 shrink-0 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&q=80&auto=format&fit=crop"
                className="w-full h-full object-cover"
                alt="Produto"
              />
            </div>
            <div className="flex-1 p-2 flex flex-col justify-between">
              <div>
                <span className="text-[8px] font-bold text-rose-600">🔥 Mais Pedido</span>
                <strong className="text-[10px] font-bold text-slate-900 block leading-tight">Bruto Bacon Triple</strong>
              </div>
              <div className="flex items-center justify-between">
                <strong className="text-xs font-black text-slate-900">R$ 38,90</strong>
                <button className="bg-[#0061FF] text-white text-[8px] font-bold px-2 py-1 rounded-lg">Pedir</button>
              </div>
            </div>
          </div>

          {/* Secondary links */}
          {[
            { icon: ShoppingBag, label: 'Cardápio PWA sem Taxas', badge: '0% taxa', bc: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
            { icon: MapPin,      label: 'Google Maps & Rotas',    badge: '⭐ 4.9',   bc: 'text-amber-700 bg-amber-50 border-amber-100' },
            { icon: Calendar,   label: 'Agendar Atendimento',    badge: 'Online',  bc: 'text-blue-700 bg-blue-50 border-blue-100' },
          ].map((link, i) => (
            <div key={i} className="bg-white border border-slate-200/80 rounded-xl px-2.5 py-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-900">
                <link.icon className="w-3.5 h-3.5 text-slate-500" />
                {link.label}
              </div>
              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-lg border ${link.bc}`}>{link.badge}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-3 py-2 text-center shrink-0">
          <span className="text-[8px] text-slate-400 font-medium">
            Powered by <strong className="text-slate-700">Linkwi · YouMachine</strong>
          </span>
        </div>

      </div>
    </div>
  );
};
